import {AxiosResponse} from "axios";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IApiResponse, IHttpClientService} from "data/services/http";
import type {ILineup} from "data/stores/team/team.store";
import {ITradeObj} from "data/providers/api/trade.api.provider";

export interface ITeamResponse {
	id: number | null;
	name: string | null;
	points: number | null;
	margin: IMargin;
	lineup: ILineup | null;
	userId: number | null;
	gamedayId: number | null;
	availableTrades: number;
	totalTrades: number;
	trades: ITradeObj[];
}

export interface IMargin {
	europe: number | null;
	usa: number | null;
}

export interface IGetTeamPayload {
	roundId: number;
}

export interface ISetTeamPayload {
	lineup: ILineup;
	gamedayId: number;
	margin?: number | null;
}

export interface IAutoPickTeamPayload {
	gamedayId: number;
	lineup?: ILineup;
	margin?: number | null;
}

export interface ITiebreakerPayload {
	teamId: number;
	marginEu: number;
	marginUsa: number;
}

export interface IUserTeamPayload {
	roundId: number;
	userId: number;
}

export interface IUserTeamResponse {
	name: string | null;
	points: number | null;
	lineup: ILineup | null;
	userId: number | null;
	gamedayId: number | null;
	gamedayPoints: number | null;
	gamedayRank: number | null;
	overallPoints: number | null;
	overallRank: number | null;
}

export interface ICaptainPayload {
	captainId: number;
	teamId: number;
}

export type TTeamResponse = IApiResponse<{team: ITeamResponse}>;
export type TUserTeamResponse = IApiResponse<{team: IUserTeamResponse}>;

export interface ITeamApiProvider {
	getTeam: (params: IGetTeamPayload) => Promise<AxiosResponse<TTeamResponse>>;
	setTeam: (params: ISetTeamPayload) => Promise<AxiosResponse<TTeamResponse>>;
	autoPickTeam: (params: IAutoPickTeamPayload) => Promise<AxiosResponse<TTeamResponse>>;
	setTiebreaker: (params: ITiebreakerPayload) => Promise<AxiosResponse<TTeamResponse>>;
	userTeam: (params: IUserTeamPayload) => Promise<AxiosResponse<TUserTeamResponse>>;
	updateCaptain: (params: ICaptainPayload) => Promise<AxiosResponse<TTeamResponse>>;
}

@injectable()
export class TeamApiProvider implements ITeamApiProvider {
	constructor(@inject(Bindings.ApiHTTPClient) private _http: IHttpClientService) {}

	getTeam = ({roundId}: IGetTeamPayload) =>
		this._http.get<TTeamResponse>(`fantasy/team/${roundId}`);
	setTeam = (params: ISetTeamPayload) => this._http.post<TTeamResponse>(`fantasy/team`, params);
	autoPickTeam = (params: IAutoPickTeamPayload) =>
		this._http.post<TTeamResponse>(`fantasy/team/autopick`, params);
	setTiebreaker = (params: ITiebreakerPayload) =>
		this._http.post<TTeamResponse>("fantasy/team/margin", params);
	userTeam = ({roundId, userId}: IUserTeamPayload) =>
		this._http.get<TUserTeamResponse>(`fantasy/team/${roundId}/${userId}`);
	updateCaptain = (params: ICaptainPayload) =>
		this._http.post<TTeamResponse>("fantasy/team/captain", params);
}
